import { Button, cn } from '@seen/ui'
import { IconTrash, IconUnlink } from '@tabler/icons-react'
import type { ComponentPropsWithoutRef, JSX, PropsWithChildren } from 'react'
import { Card } from '#src/components/core/Card.tsx'
import { useDeleteConfirmationStore } from '#src/hooks/useDeleteConfirmationStore.ts'

interface CarePlanCardProps extends ComponentPropsWithoutRef<typeof Card> {
  showActions?: boolean
  onDelete?: () => void
  onUnlink?: () => void
  name?: string
  type?: 'goal' | 'condition' | 'outcome'
}

export function CarePlanCard({
  children,
  showActions,
  onDelete,
  onUnlink,
  name,
  type,
  ...props
}: PropsWithChildren<CarePlanCardProps>): JSX.Element {
  const hasActions = onDelete ?? onUnlink
  const open = useDeleteConfirmationStore((state) => state.open)

  if (!type) {
    return <Card {...props}>{children}</Card>
  }

  const groupClasses = {
    goal: 'group/goal',
    condition: 'group/condition',
    outcome: 'group/outcome',
  }

  return (
    <div className={cn('relative', groupClasses[type])}>
      {showActions && hasActions && (
        <div
          className={cn(
            '-top-12 absolute right-0 z-10',
            'flex gap-2 rounded-md border border-zinc-200 bg-white',
            'opacity-0 shadow-sm transition-opacity',
            {
              'group-hover/goal:opacity-100': type === 'goal',
              'group-hover/condition:opacity-100': type === 'condition',
              'group-hover/outcome:opacity-100': type === 'outcome',
            }
          )}
        >
          {onUnlink && (
            <Button key="unlink" variant="ghost" size="icon" onClick={onUnlink}>
              <IconUnlink size={16} />
            </Button>
          )}
          {onDelete && (
            <Button
              key="delete"
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation()
                open({
                  title: `Delete "${name ?? 'Resource'}?"`,
                  description:
                    'Deleted resources can be recovered by contacting our team.',
                  onConfirm: onDelete,
                })
              }}
            >
              <IconTrash size={16} />
            </Button>
          )}
        </div>
      )}
      <Card {...props}>{children}</Card>
    </div>
  )
}
